import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocalStorageObject } from "@/shared/hooks/useLocalStorageObject";
import { useTimeoutNotificationTimer } from "@/shared/hooks/useTimeoutNotificationTimer";
import { LoadingNotification } from "@/shared/components/common/ui/LoadingNotification";
import { ToastNotification } from "@/shared/components/common/toast/ToastNotification";
import type { ReportPayload } from "@/signalR/jobNotifications";

interface ExportNotificationContextValue {
  isExporting: boolean;
  exportSeconds: number;
  exportingCorrelationId: string | null;
  exportPayload: ReportPayload | null;
  setExportingCorrelationId: (id: string | null) => void;
  setExportPayload: (payload: ReportPayload | null) => void;
  setExportStartTime: (time: number | null) => void;
  setEmailStartTime: (time: number | null) => void;
  isEmailSending: boolean;
  emailSeconds: number;
  emailSendingCorrelationId: string | null;
  emailPayload: ReportPayload | null;
  setEmailSendingCorrelationId: (id: string | null) => void;
  setEmailPayload: (payload: ReportPayload | null) => void;

  showToast: (message: string, type?: "success" | "error") => void;
}

const CostReportNotificationContext =
  createContext<ExportNotificationContextValue | null>(null);

export const CostReportNotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [exportingCorrelationId, setExportingCorrelationId] =
    useLocalStorageObject<string | null>("export_correlation_id", null);
  const [exportPayload, setExportPayload] =
    useLocalStorageObject<ReportPayload | null>("export_payload", null);
  const [exportStartTime, setExportStartTime] = useLocalStorageObject<
    number | null
  >("export_start_time", null);
  const isExporting = exportingCorrelationId !== null;
  const [emailSendingCorrelationId, setEmailSendingCorrelationId] =
    useLocalStorageObject<string | null>("email_correlation_id", null);
  const [emailPayload, setEmailPayload] =
    useLocalStorageObject<ReportPayload | null>("email_payload", null);
  const [emailStartTime, setEmailStartTime] = useLocalStorageObject<
    number | null
  >("email_start_time", null);
  const isEmailSending = emailSendingCorrelationId !== null;
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const TIMEOUT_SECONDS = import.meta.env.VITE_REPORT_TIMEOUT_SECONDS;


  const showToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    // Clear previous timeout if it exists
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, type });
    // Set new timeout and store reference
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 5000);
  };

  // Normalize or clear any invalid timestamps persisted in localStorage
  useEffect(() => {
    const normalize = (val: any) => {
      if (val == null) return null;
      let n = Number(val);
      if (Number.isNaN(n) || n <= 0) return null;
      if (n < 1_000_000_000_000) n = n * 1000; // seconds -> ms
      if (n - Date.now() > 60 * 1000) return null; // too far in future
      return n;
    };

    const ns = normalize(exportStartTime);
    if (ns === null && exportStartTime !== null) setExportStartTime(null);
    else if (ns !== null && ns !== exportStartTime) setExportStartTime(ns);

    const ne = normalize(emailStartTime);
    if (ne === null && emailStartTime !== null) setEmailStartTime(null);
    else if (ne !== null && ne !== emailStartTime) setEmailStartTime(ne);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Use shared timeout hook for export and email timers
  const exportSeconds = useTimeoutNotificationTimer(
    isExporting,
    exportStartTime,
    TIMEOUT_SECONDS,
    () => {
      setExportingCorrelationId(null);
      setExportPayload(null);
      setExportStartTime(null);
      showToast("Something went wrong, Please try again.", "error");
    },
  );

  const emailSeconds = useTimeoutNotificationTimer(
    isEmailSending,
    emailStartTime,
    TIMEOUT_SECONDS,
    () => {
      setEmailSendingCorrelationId(null);
      setEmailPayload(null);
      setEmailStartTime(null);
      showToast("Something went wrong, Please try again.", "error");
    },
  );

  return (
    <CostReportNotificationContext.Provider
      value={{
        isExporting,
        exportSeconds,
        exportingCorrelationId,
        exportPayload,
        setExportingCorrelationId,
        setExportPayload,
        isEmailSending,
        emailSeconds,
        emailSendingCorrelationId,
        emailPayload,
        setEmailSendingCorrelationId,
        setEmailPayload,
        setExportStartTime,
        setEmailStartTime,
        showToast,
      }}
    >
      {children}

      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 items-end">
        <LoadingNotification
          isVisible={isExporting}
          message="Generating report"
          seconds={exportSeconds}
        />
        <LoadingNotification
          isVisible={isEmailSending}
          message="Sending email"
          seconds={emailSeconds}
        />
        <ToastNotification
          message={toast?.message ?? null}
          type={toast?.type}
        />
      </div>
    </CostReportNotificationContext.Provider>
  );
};

export const useCostReportNotification = () => {
  const ctx = useContext(CostReportNotificationContext);
  if (!ctx)
    throw new Error(
      "useCostReportNotification must be used inside CostReportNotificationProvider",
    );
  return ctx;
};
